/**
 * Custom hook for creating job applications using TanStack Query
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { apiPost, ApiClientError } from '@/lib/api/api-client'
import type { Application, CreateApplicationRequest } from '@/types'

/**
 * Creates a new job application
 */
async function createApplication(
  payload: CreateApplicationRequest
): Promise<Application> {
  return apiPost<Application>('/recruitment/applications', payload)
}

/**
 * Hook for creating job applications with retry logic
 *
 * @returns TanStack Query mutation result
 */
export function useCreateApplication(): UseMutationResult<
  Application,
  ApiClientError,
  CreateApplicationRequest
> {
  return useMutation({
    mutationFn: createApplication,
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors - won't be fixed by retrying)
      if (error.statusCode >= 400 && error.statusCode < 500) {
        return false
      }
      // Retry up to 3 times for 5xx errors (server errors)
      return failureCount < 3
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}
